import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ScheduleSettings from './ScheduleSettings';

const API = process.env.REACT_APP_API_URL || '/api';

export default function SettingsTab({ settings, setSettings, t }) {
  // Local UI state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState({ type: '', text: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingGoogle, setTestingGoogle] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [openAccordion, setOpenAccordion] = useState('google');
  const [showGoogleHelp, setShowGoogleHelp] = useState(false);
  const [showTwilioHelp, setShowTwilioHelp] = useState(false);
  const [translationTest, setTranslationTest] = useState({ loading: false, result: null, error: null });
  const [testEmailState, setTestEmailState] = useState({ loading: false, msg: '' });
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [showStripeHelp, setShowStripeHelp] = useState(false);

  // Load settings on mount
  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Save handler
  const saveSettings = async () => {
    setSettingsLoading(true);
    setSettingsMsg({ type: '', text: '' });
    try {
      await axios.put(`${API}/admin/settings`, settings);
      setSettingsMsg({ type: 'success', text: t.admin.settings.saveSuccess });
      loadSettings();
    } catch (err) {
      setSettingsMsg({ type: 'danger', text: t.admin.settings.saveError });
    } finally {
      setSettingsLoading(false);
    }
  };

  const testGoogleConnection = async () => {
    setTestingGoogle(true);
    try {
      const res = await axios.get(`${API}/google-reviews`);
      if (res.data.configured && res.data.reviews && res.data.reviews.length > 0) {
        setSettingsMsg({ type: 'success', text: t.admin.settings.testSuccess });
      } else {
        setSettingsMsg({ type: 'warning', text: t.admin.settings.testFail });
      }
    } catch (err) {
      setSettingsMsg({ type: 'danger', text: t.admin.settings.testFail });
    } finally {
      setTestingGoogle(false);
    }
  };

  const testTranslation = async () => {
    setTranslationTest({ loading: true, result: null, error: null });
    try {
      const res = await axios.post(`${API}/admin/translate`, {
        text: 'Bonjour, ceci est un test.',
        source_lang: 'fr',
        target_lang: 'en'
      });
      setTranslationTest({ loading: false, result: res.data.translated || res.data.text, error: null });
    } catch (err) {
      setTranslationTest({ loading: false, result: null, error: err.response?.data?.error || err.message });
    }
  };

  return (
              <div>
                <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '2rem' }}>{t.admin.settings.title}</h2>
                <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '2rem', maxWidth: '700px' }}>

                  {settingsMsg.text && (
                      <div className={`alert alert-${settingsMsg.type}`} role="alert">
                        {settingsMsg.text}
                      </div>
                  )}

                  <div className="accordion accordion-flush">

                    {/* Section 1: Google Reviews */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'google' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'google' ? null : 'google')}
                            aria-expanded={openAccordion === 'google'}
                        >
                          {t.admin.settings.googleSection}
                        </button>
                      </h2>
                      {openAccordion === 'google' && (
                        <div className="accordion-body">
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.googleApiKey}</label>
                            <div className="input-group">
                              <input
                                  type={showApiKey ? 'text' : 'password'}
                                  className="form-control"
                                  value={settings.google_places_api_key}
                                  onChange={e => setSettings({ ...settings, google_places_api_key: e.target.value })}
                              />
                              <button className="btn btn-outline-primary" type="button" aria-label="Toggle API key visibility" onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? '🙈' : '👁'}
                              </button>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.googlePlaceId}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={settings.google_place_id}
                                onChange={e => setSettings({ ...settings, google_place_id: e.target.value })}
                            />
                          </div>

                          <button className="btn btn-outline-primary btn-sm mb-3" onClick={testGoogleConnection} disabled={testingGoogle}>
                            {testingGoogle ? t.admin.settings.testing : t.admin.settings.testGoogle}
                          </button>

                          <div>
                            <span
                                className="form-text"
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setShowGoogleHelp(!showGoogleHelp)}
                                role="button"
                            >
                              {t.admin.settings.googleHelpTitle}
                            </span>
                            {showGoogleHelp && (
                              <div className="mt-2">
                                <div className="form-text" style={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                  {t.admin.settings.googleHelp}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section 2: Scheduling */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'scheduling' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'scheduling' ? null : 'scheduling')}
                            aria-expanded={openAccordion === 'scheduling'}
                        >
                          {t.admin.settings.schedulingSection}
                        </button>
                      </h2>
                      {openAccordion === 'scheduling' && (
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-12 col-md-6 mb-3">
                              <label className="form-label">{t.admin.settings.mechanicsCount}</label>
                              <input
                                  type="number"
                                  className="form-control"
                                  min="1"
                                  value={settings.mechanics_count}
                                  onChange={e => setSettings({ ...settings, mechanics_count: e.target.value })}
                              />
                            </div>
                            <div className="col-12 col-md-6 mb-3">
                              <label className="form-label">{t.admin.settings.slotInterval}</label>
                              <input
                                  type="number"
                                  className="form-control"
                                  min="5"
                                  step="5"
                                  value={settings.slot_interval_minutes}
                                  onChange={e => setSettings({ ...settings, slot_interval_minutes: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section 3: Notifications */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'notifications' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'notifications' ? null : 'notifications')}
                            aria-expanded={openAccordion === 'notifications'}
                        >
                          {t.admin.settings.notificationSection}
                        </button>
                      </h2>
                      {openAccordion === 'notifications' && (
                        <div className="accordion-body">
                          <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="enableSound"
                                checked={settings.enable_notification_sound === 'true'}
                                onChange={e => setSettings({ ...settings, enable_notification_sound: e.target.checked ? 'true' : 'false' })}
                            />
                            <label className="form-check-label" htmlFor="enableSound">
                              {t.admin.notificationSettings.enableSound}
                            </label>
                          </div>

                          <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="enableSms"
                                checked={settings.enable_sms_notifications === 'true'}
                                onChange={e => setSettings({ ...settings, enable_sms_notifications: e.target.checked ? 'true' : 'false' })}
                            />
                            <label className="form-check-label" htmlFor="enableSms">
                              {t.admin.notificationSettings.enableSms}
                            </label>
                          </div>

                          {settings.enable_sms_notifications === 'true' && (
                              <div className="mb-3">
                                <label className="form-label">{t.admin.notificationSettings.adminPhone}</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    placeholder={t.admin.notificationSettings.adminPhonePlaceholder}
                                    value={settings.admin_notification_phone}
                                    onChange={e => setSettings({ ...settings, admin_notification_phone: e.target.value })}
                                />
                              </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Section 4: Twilio — only when SMS enabled */}
                    {settings.enable_sms_notifications === 'true' && (
                        <div className="accordion-item">
                          <h2 className="accordion-header">
                            <button
                                className={`accordion-button ${openAccordion !== 'twilio' ? 'collapsed' : ''}`}
                                type="button"
                                onClick={() => setOpenAccordion(openAccordion === 'twilio' ? null : 'twilio')}
                                aria-expanded={openAccordion === 'twilio'}
                            >
                              {t.admin.settings.twilioSection}
                            </button>
                          </h2>
                          {openAccordion === 'twilio' && (
                            <div className="accordion-body">
                              <div className="mb-3">
                                <label className="form-label">{t.admin.settings.twilioSid}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.twilio_account_sid}
                                    onChange={e => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                                    placeholder="AC..."
                                />
                              </div>

                              <div className="mb-3">
                                <label className="form-label">{t.admin.settings.twilioAuthToken}</label>
                                <div className="input-group">
                                  <input
                                      type={showTwilioToken ? 'text' : 'password'}
                                      className="form-control"
                                      value={settings.twilio_auth_token}
                                      onChange={e => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                                  />
                                  <button className="btn btn-outline-primary" type="button" aria-label="Toggle token visibility" onClick={() => setShowTwilioToken(!showTwilioToken)}>
                                    {showTwilioToken ? '🙈' : '👁'}
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                <label className="form-label">{t.admin.settings.twilioFromNumber}</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    value={settings.twilio_from_number}
                                    onChange={e => setSettings({ ...settings, twilio_from_number: e.target.value })}
                                    placeholder="+15551234567"
                                />
                              </div>

                              <div>
                                <span
                                    className="form-text"
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => setShowTwilioHelp(!showTwilioHelp)}
                                    role="button"
                                >
                                  {t.admin.settings.twilioHelpTitle}
                                </span>
                                {showTwilioHelp && (
                                  <div className="mt-2">
                                    <div className="form-text" style={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                      {t.admin.settings.twilioHelp}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                    )}

                    {/* Section 5: Email Notifications */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'email' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'email' ? null : 'email')}
                            aria-expanded={openAccordion === 'email'}
                        >
                          {t.admin.settings.emailSection || 'Email Notifications'}
                        </button>
                      </h2>
                      {openAccordion === 'email' && (
                        <div className="accordion-body">
                          <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="enableEmailNotif"
                                checked={settings.enable_email_notifications === 'true'}
                                onChange={e => setSettings({ ...settings, enable_email_notifications: e.target.checked ? 'true' : 'false' })}
                            />
                            <label className="form-check-label" htmlFor="enableEmailNotif">
                              {t.admin.settings.enableEmailNotifications || 'Enable email notifications to customers'}
                            </label>
                          </div>
                          {settings.enable_email_notifications === 'true' && (
                            <>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.emailFromAddress || 'From Email Address'}</label>
                              <input
                                  type="email"
                                  className="form-control"
                                  value={settings.email_from_address}
                                  onChange={e => setSettings({ ...settings, email_from_address: e.target.value })}
                                  placeholder="noreply@mecaniqueautocjpa.com"
                              />
                              <div className="form-text">{t.admin.settings.emailFromHint || 'The sender email address for outgoing notifications.'}</div>
                            </div>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.emailProvider || 'Email Provider'}</label>
                              <select className="form-select" value={settings.email_provider || 'smtp'} onChange={e => setSettings({ ...settings, email_provider: e.target.value })}>
                                <option value="smtp">SMTP</option>
                                <option value="gmail_api">Gmail API (Google Workspace)</option>
                              </select>
                              <div className="form-text">{settings.email_provider === 'gmail_api' ? (t.admin.settings.gmailApiHint || 'Uses Google Gmail API over HTTPS. Required for Railway/cloud hosting where SMTP ports are blocked.') : (t.admin.settings.smtpHint || 'Direct SMTP connection. Works on VPS/self-hosted servers.')}</div>
                            </div>
                            {(settings.email_provider || 'smtp') === 'smtp' ? (
                            <>
                            <div className="row">
                              <div className="col-md-8 mb-3">
                                <label className="form-label">{t.admin.settings.smtpHost || 'SMTP Host'}</label>
                                <input type="text" className="form-control" value={settings.smtp_host || ''} onChange={e => setSettings({ ...settings, smtp_host: e.target.value })} placeholder="smtp.gmail.com" />
                              </div>
                              <div className="col-md-4 mb-3">
                                <label className="form-label">{t.admin.settings.smtpPort || 'SMTP Port'}</label>
                                <input type="number" className="form-control" value={settings.smtp_port || ''} onChange={e => setSettings({ ...settings, smtp_port: e.target.value })} placeholder="587" />
                              </div>
                            </div>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.smtpUser || 'SMTP User'}</label>
                              <input type="email" className="form-control" value={settings.smtp_user || ''} onChange={e => setSettings({ ...settings, smtp_user: e.target.value })} placeholder="noreply@mecaniqueautocjpa.com" />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.smtpPassword || 'SMTP Password'}</label>
                              <input type="password" className="form-control" value={settings.smtp_password || ''} onChange={e => setSettings({ ...settings, smtp_password: e.target.value })} placeholder="••••••••" />
                            </div>
                            </>
                            ) : (
                            <>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.gmailClientId || 'Gmail Client ID'}</label>
                              <input type="text" className="form-control" value={settings.gmail_client_id || ''} onChange={e => setSettings({ ...settings, gmail_client_id: e.target.value })} placeholder="xxxx.apps.googleusercontent.com" />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.gmailClientSecret || 'Gmail Client Secret'}</label>
                              <input type="password" className="form-control" value={settings.gmail_client_secret || ''} onChange={e => setSettings({ ...settings, gmail_client_secret: e.target.value })} placeholder="••••••••" />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.gmailRefreshToken || 'Gmail Refresh Token'}</label>
                              <input type="password" className="form-control" value={settings.gmail_refresh_token || ''} onChange={e => setSettings({ ...settings, gmail_refresh_token: e.target.value })} placeholder="••••••••" />
                              <div className="form-text">{t.admin.settings.gmailTokenHint || 'Get this from Google OAuth Playground. See setup guide below.'}</div>
                            </div>
                            <div className="alert alert-info small mb-3">
                              <strong>{t.admin.settings.gmailSetupGuide || 'Setup Guide'}:</strong>
                              <ol className="mb-0 mt-1 ps-3">
                                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> → Create a project</li>
                                <li>Enable the <strong>Gmail API</strong></li>
                                <li>Go to Credentials → Create <strong>OAuth 2.0 Client ID</strong> (Web application)</li>
                                <li>Add <code>https://developers.google.com/oauthplayground</code> as an authorized redirect URI</li>
                                <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> into the fields above</li>
                                <li>Go to <a href="https://developers.google.com/oauthplayground" target="_blank" rel="noopener noreferrer">OAuth Playground</a></li>
                                <li>Click the gear icon ⚙️ → Check "Use your own OAuth credentials" → Enter your Client ID &amp; Secret</li>
                                <li>In Step 1, enter scope: <code>https://www.googleapis.com/auth/gmail.send</code></li>
                                <li>Authorize with your <strong>noreply@mecaniqueautocjpa.com</strong> Google Workspace account</li>
                                <li>In Step 2, click "Exchange authorization code for tokens" → Copy the <strong>Refresh Token</strong></li>
                              </ol>
                            </div>
                            </>
                            )}
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <button type="button" className="btn btn-outline-primary btn-sm" disabled={testEmailState.loading} onClick={async () => {
                                setTestEmailState({ loading: true, msg: '' });
                                try {
                                  // Save settings first so SMTP credentials are persisted
                                  await axios.put(`${API}/admin/settings`, settings);
                                  const res = await axios.post(`${API}/admin/test-email`);
                                  setTestEmailState({ loading: false, msg: (t.admin.settings.testEmailSuccess || 'Test email sent!') + ' (' + (res.data.message || '') + ')' });
                                } catch (err) {
                                  setTestEmailState({ loading: false, msg: (t.admin.settings.testEmailFailed || 'Test email failed') + ': ' + (err.response?.data?.message || err.message) });
                                }
                              }}>
                                {testEmailState.loading ? (t.admin.settings.testEmailSending || 'Sending...') : (t.admin.settings.testEmail || 'Send Test Email')}
                              </button>
                              {testEmailState.msg && (
                                <span style={{ fontSize: '0.85rem', color: testEmailState.msg.includes('failed') || testEmailState.msg.includes('Failed') || testEmailState.msg.includes('Échec') ? '#D42B2B' : '#22c55e' }}>
                                  {testEmailState.msg}
                                </span>
                              )}
                            </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Section 6: Customer SMS */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'customerSms' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'customerSms' ? null : 'customerSms')}
                            aria-expanded={openAccordion === 'customerSms'}
                        >
                          {t.admin.settings.customerSmsSection || 'Customer SMS Notifications'}
                        </button>
                      </h2>
                      {openAccordion === 'customerSms' && (
                        <div className="accordion-body">
                          <div className="form-check form-switch mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="enableCustomerSms"
                                checked={settings.enable_customer_sms_notifications === 'true'}
                                onChange={e => setSettings({ ...settings, enable_customer_sms_notifications: e.target.checked ? 'true' : 'false' })}
                            />
                            <label className="form-check-label" htmlFor="enableCustomerSms">
                              {t.admin.settings.enableCustomerSms || 'Enable SMS notifications to customers'}
                            </label>
                          </div>
                          <div className="form-text">{t.admin.settings.customerSmsHint || 'Requires Twilio credentials configured above. Only sends to customers with verified phone numbers.'}</div>
                        </div>
                      )}
                    </div>

                    {/* Section 7: Shop Information */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'shopInfo' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'shopInfo' ? null : 'shopInfo')}
                            aria-expanded={openAccordion === 'shopInfo'}
                        >
                          {t.admin.settings.shopInfoSection || 'Shop Information'}
                        </button>
                      </h2>
                      {openAccordion === 'shopInfo' && (
                        <div className="accordion-body">
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.shopName || 'Shop Name'}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={settings.shop_name}
                                onChange={e => setSettings({ ...settings, shop_name: e.target.value })}
                                placeholder="Mécanique Auto CJPA"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.shopAddress || 'Shop Address'}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={settings.shop_address}
                                onChange={e => setSettings({ ...settings, shop_address: e.target.value })}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.shopPhone || 'Shop Phone'} 1</label>
                            <input
                                type="tel"
                                className="form-control"
                                value={settings.shop_phone}
                                onChange={e => setSettings({ ...settings, shop_phone: e.target.value })}
                                placeholder="+1 514-327-2107"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.shopPhone || 'Shop Phone'} 2</label>
                            <input
                                type="tel"
                                className="form-control"
                                value={settings.shop_phone2}
                                onChange={e => setSettings({ ...settings, shop_phone2: e.target.value })}
                                placeholder="+1 514-582-7527"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.shopTimezone || 'Timezone'}</label>
                            <select
                                className="form-select"
                                value={settings.shop_timezone}
                                onChange={e => setSettings({ ...settings, shop_timezone: e.target.value })}
                            >
                              <option value="America/Toronto">America/Toronto (Eastern)</option>
                              <option value="America/Montreal">America/Montreal (Eastern)</option>
                              <option value="America/Vancouver">America/Vancouver (Pacific)</option>
                              <option value="America/Winnipeg">America/Winnipeg (Central)</option>
                              <option value="America/Edmonton">America/Edmonton (Mountain)</option>
                              <option value="America/Halifax">America/Halifax (Atlantic)</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section 8: Business Schedule */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'schedule' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'schedule' ? null : 'schedule')}
                            aria-expanded={openAccordion === 'schedule'}
                        >
                          {t.admin.settings.scheduleSection || 'Business Schedule'}
                        </button>
                      </h2>
                      {openAccordion === 'schedule' && (
                        <div className="accordion-body">
                          <ScheduleSettings />
                        </div>
                      )}
                    </div>

                    {/* Section: Translation Provider */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                            className={`accordion-button ${openAccordion !== 'translation' ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => setOpenAccordion(openAccordion === 'translation' ? null : 'translation')}
                            aria-expanded={openAccordion === 'translation'}
                        >
                          {t.admin.settings.translationSection}
                        </button>
                      </h2>
                      {openAccordion === 'translation' && (
                        <div className="accordion-body">
                          <p className="form-text mb-3">{t.admin.settings.translationHelp}</p>
                          <div className="mb-3">
                            <label className="form-label">{t.admin.settings.translationProvider}</label>
                            <select className="form-select" value={settings.translation_provider} onChange={e => setSettings({ ...settings, translation_provider: e.target.value })}>
                              <option value="none">{t.admin.settings.translationProviderNone}</option>
                              <option value="deepl">{t.admin.settings.translationProviderDeepl}</option>
                              <option value="google">{t.admin.settings.translationProviderGoogle}</option>
                            </select>
                          </div>
                          {settings.translation_provider !== 'none' && (
                            <div className="mb-3">
                              <label className="form-label">{t.admin.settings.translationApiKey}</label>
                              <input type="password" className="form-control" value={settings.translation_api_key} onChange={e => setSettings({ ...settings, translation_api_key: e.target.value })} />
                            </div>
                          )}
                          {settings.translation_provider !== 'none' && (
                            <div className="mb-3">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={testTranslation}
                                disabled={translationTest.loading || !settings.translation_api_key}
                              >
                                {translationTest.loading ? t.admin.settings.testingTranslation : t.admin.settings.testTranslation}
                              </button>
                              {translationTest.result && (
                                <div className="mt-2" style={{ color: '#198754', fontSize: '0.875rem' }}>
                                  {t.admin.settings.translationSuccess}: {translationTest.result}
                                </div>
                              )}
                              {translationTest.error && (
                                <div className="mt-2" style={{ color: '#dc3545', fontSize: '0.875rem' }}>
                                  {t.admin.settings.translationFailed}: {translationTest.error}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Section: Stripe Payments */}
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          className={`accordion-button ${openAccordion !== 'stripe' ? 'collapsed' : ''}`}
                          type="button"
                          onClick={() => setOpenAccordion(openAccordion === 'stripe' ? null : 'stripe')}
                          aria-expanded={openAccordion === 'stripe'}
                        >
                          {t.admin.settings.stripeSection}
                        </button>
                      </h2>
                      {openAccordion === 'stripe' && (
                        <div className="accordion-body">
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="enableDeposits"
                              checked={settings.enable_deposits === 'true'}
                              onChange={e => setSettings({ ...settings, enable_deposits: e.target.checked ? 'true' : 'false' })}
                            />
                            <label className="form-check-label" htmlFor="enableDeposits">
                              {t.admin.settings.enableDeposits}
                            </label>
                            <div className="form-text">{t.admin.settings.enableDepositsHelp}</div>
                          </div>

                          {settings.enable_deposits === 'true' && (
                            <>
                              <div className="mb-3">
                                <label className="form-label">{t.admin.settings.stripeSecretKey}</label>
                                <div className="input-group">
                                  <input
                                    type={showStripeKey ? 'text' : 'password'}
                                    className="form-control"
                                    value={settings.stripe_secret_key || ''}
                                    onChange={e => setSettings({ ...settings, stripe_secret_key: e.target.value })}
                                    placeholder="sk_live_... or sk_test_..."
                                  />
                                  <button className="btn btn-outline-primary" type="button" aria-label="Toggle Stripe key visibility" onClick={() => setShowStripeKey(!showStripeKey)}>
                                    {showStripeKey ? '🙈' : '👁'}
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                <label className="form-label">{t.admin.settings.stripeWebhookSecret}</label>
                                <div className="input-group">
                                  <input
                                    type={showWebhookSecret ? 'text' : 'password'}
                                    className="form-control"
                                    value={settings.stripe_webhook_secret || ''}
                                    onChange={e => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
                                    placeholder="whsec_..."
                                  />
                                  <button className="btn btn-outline-primary" type="button" aria-label="Toggle webhook secret visibility" onClick={() => setShowWebhookSecret(!showWebhookSecret)}>
                                    {showWebhookSecret ? '🙈' : '👁'}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}

                          <div>
                            <span
                              className="form-text"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => setShowStripeHelp(!showStripeHelp)}
                              role="button"
                            >
                              {t.admin.settings.stripeHelpTitle}
                            </span>
                            {showStripeHelp && (
                              <div className="mt-2">
                                <div className="form-text" style={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                                  {t.admin.settings.stripeHelp}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-primary" onClick={saveSettings} disabled={settingsLoading}>
                      {settingsLoading ? t.admin.settings.saving : t.admin.settings.save}
                    </button>
                  </div>
                </div>
              </div>
  );
}
